'use client'

import { useState } from 'react'
import { addToCart } from '@/lib/cart'
import type { Product, Color } from '@/lib/types'

interface AddToCartButtonProps {
  product: Product
  colors: Color[]
  selectedColorId?: string
}

export default function AddToCartButton({
  product,
  colors,
  selectedColorId,
}: AddToCartButtonProps) {
  const [quantity, setQuantity] = useState(1)
  const [isAdding, setIsAdding] = useState(false)

  const colorId = selectedColorId || colors[0]?.id || ''

  const handleAddToCart = () => {
    const selectedColor = colors.find((c) => c.id === colorId)
    if (!selectedColor) {
      alert('Please select a color')
      return
    }

    setIsAdding(true)

    addToCart({
      productId: product.id,
      productName: product.name,
      productSlug: product.slug,
      priceCents: product.price_cents,
      colorId: selectedColor.id,
      colorName: selectedColor.name,
      quantity,
      imagePath: product.images?.[0]?.path,
    })

    setTimeout(() => {
      setIsAdding(false)
      setQuantity(1)
    }, 500)
  }

  return (
    <div className="space-y-5">
      {/* Quantity Selector */}
      <div className="bg-white rounded-3xl shadow-soft p-4">
        <label className="block text-sm font-medium text-gray-700 mb-3">Quantity</label>
        <div className="flex items-center gap-4">
          <button
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            className="w-12 h-12 rounded-2xl border border-gray-200 bg-cloud hover:bg-peach flex items-center justify-center text-lg font-semibold"
          >
            -
          </button>
          <span className="text-lg font-semibold w-12 text-center">{quantity}</span>
          <button
            onClick={() => setQuantity(quantity + 1)}
            className="w-12 h-12 rounded-2xl border border-gray-200 bg-cloud hover:bg-peach flex items-center justify-center text-lg font-semibold"
          >
            +
          </button>
        </div>
      </div>

      {/* Add to Cart Button */}
      <button
        onClick={handleAddToCart}
        disabled={isAdding || colors.length === 0}
        className="w-full bg-primary text-white py-4 rounded-full font-semibold shadow-soft hover:shadow-pop transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isAdding ? 'Added! ✓' : 'Add to Cart'}
      </button>
    </div>
  )
}
